import blueTray from '@assets/images/icons/blueTray.svg';
import DefaultButton from '@components/Atoms/DefaultButton';
import Img from '@components/Atoms/Img';
import Modal from '@components/Modal';
import {
  useCreateFreedomToSpeakUpResponse,
  useFreedomToSpeakUpConcern,
  useFreedomToSpeakUpResponses,
  useMarkFreedomToSpeakUpResponsesSeen,
} from '@hooks/api/useFreedomToSpeakUp';
import { useAppDispatch } from '@hooks/useAppDispatch';
import SendIcon from '@mui/icons-material/Send';
import { CircularProgress, Skeleton } from '@mui/material';
import { showToast } from '@shared/atoms';
import classNames from 'classnames';
import { ToastMessages } from 'constants/toastMessages';
import React, { useEffect, useRef, useState } from 'react';
import { refreshConfigurationSliceActions } from 'store/Slices/RefreshCofigurationsSlice';
import messagingStyles from '../InstantMessagingModal/index.module.scss';

export interface FreedomToSpeakUpMessagingModalProps {
  concernId: number;
  referenceNumber: string;
  isOpen: boolean;
  onClose: () => void;
}

const formatDateTime = (isoString: string) => {
  const date = new Date(isoString);
  const hours = date.getHours();
  const minutes = date.getMinutes();

  const formattedTime = `${hours % 12 || 12}:${minutes < 10 ? `0${minutes}` : minutes} ${
    hours >= 12 ? 'PM' : 'AM'
  }`;
  const formattedDate = `${date.toLocaleDateString()}`;

  return { formattedTime, formattedDate };
};

export const FreedomToSpeakUpMessagingModal: React.FC<FreedomToSpeakUpMessagingModalProps> = ({
  concernId,
  referenceNumber,
  isOpen,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const chatBoxRef = useRef<HTMLDivElement | null>(null);
  const markSeenForSessionRef = useRef<string | null>(null);

  const [messageText, setMessageText] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  const { data: concernData, isLoading: isLoadingConcern } = useFreedomToSpeakUpConcern(
    concernId,
    isOpen,
  );
  const {
    data: responsesData,
    isLoading: isLoadingResponses,
    refetch: refetchResponses,
  } = useFreedomToSpeakUpResponses(concernId, isOpen);

  const { mutate: markResponsesSeen } = useMarkFreedomToSpeakUpResponsesSeen();
  const createResponseMutation = useCreateFreedomToSpeakUpResponse();

  const threadItems = responsesData?.success ? responsesData.responseObject?.items ?? [] : [];
  const isClosed = concernData?.responseObject?.status?.key === 'closed';
  const conversationLoading = isLoadingConcern || isLoadingResponses;
  const loadingUserBubbleClass = classNames(
    messagingStyles.messageBubble,
    messagingStyles.userBubble,
  );
  const loadingSystemBubbleClass = classNames(
    messagingStyles.messageBubble,
    messagingStyles.systemBubble,
  );

  useEffect(() => {
    const chatBox = chatBoxRef.current;
    if (chatBox && isOpen) {
      setTimeout(() => {
        chatBox.scrollTo({
          top: chatBox.scrollHeight,
          behavior: 'smooth',
        });
      }, 100);
    }
  }, [isOpen, messageText, threadItems.length]);

  useEffect(() => {
    if (!isOpen) {
      markSeenForSessionRef.current = null;

      return;
    }

    const sessionKey = String(concernId);
    if (markSeenForSessionRef.current === sessionKey) {
      return;
    }

    markSeenForSessionRef.current = sessionKey;
    dispatch(refreshConfigurationSliceActions.resetConfigurations());
    markResponsesSeen({ concernId });
  }, [concernId, dispatch, isOpen, markResponsesSeen]);

  const closeModal = () => {
    setMessageText('');
    setErrorMessage('');
    setSendingMessage(false);
    dispatch(refreshConfigurationSliceActions.setRefreshSpeakUps(true));
    onClose();
  };

  const handleSendMessage = async () => {
    const trimmed = messageText.trim();
    if (!trimmed || isClosed) {
      return;
    }

    setSendingMessage(true);
    setErrorMessage('');

    try {
      const result = await createResponseMutation.mutateAsync({
        concernId,
        message: trimmed,
      });

      if (result?.success) {
        setMessageText('');
        await refetchResponses();
      } else {
        setErrorMessage(result?.message || 'Message failed. Try again.');
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
          ? err.message
          : 'Message failed. Try again.';
      setErrorMessage(message);
    } finally {
      setSendingMessage(false);
    }
  };

  return (
    <div className={messagingStyles.root}>
      <Modal open={isOpen} setOpen={closeModal} overlay className={messagingStyles.modalRoot}>
        <DefaultButton
          color="primary"
          type="button"
          className={messagingStyles.btnClose}
          onClick={() => {
            if (sendingMessage) {
              showToast(
                ToastMessages.instantMessaging.error.WAIT_UNTIL_PREVIOUS_MESSAGE,
                undefined,
                'info',
              );
            } else {
              closeModal();
            }
          }}
          disabled={sendingMessage}
        >
          <i className={classNames('icon', 'icon-x', messagingStyles.btnCloseIcon)} />
        </DefaultButton>

        <div className={messagingStyles.modalBody}>
          <div className={messagingStyles.modalTitle}>{referenceNumber}</div>
          <hr />

          <div className={messagingStyles.modalBody}>
            <div className={messagingStyles.chatContainer} ref={chatBoxRef}>
              {conversationLoading ? (
                <>
                  <div className={loadingUserBubbleClass}>
                    <Skeleton variant="text" className={messagingStyles.loadingText} height={20} />
                    <Skeleton variant="text" className={messagingStyles.loadingText} height={20} />
                  </div>
                  <div className={loadingSystemBubbleClass}>
                    <Skeleton variant="text" className={messagingStyles.loadingText} height={20} />
                  </div>
                </>
              ) : threadItems.length === 0 ? (
                <div className={messagingStyles.noDataSection}>
                  <Img src={blueTray} className={messagingStyles.blueTrayImg} alt="" />
                  <p>No messages to preview</p>
                </div>
              ) : (
                threadItems.map((message, index) => {
                  const { formattedTime, formattedDate } = formatDateTime(message.createdAt);
                  const isLastMessage = index === threadItems.length - 1;

                  return (
                    <div
                      key={`${message.id}-${message.createdAt}`}
                      className={classNames(
                        messagingStyles.messageBubble,
                        message.isFromCurrentUser
                          ? messagingStyles.userBubble
                          : messagingStyles.systemBubble,
                      )}
                    >
                      <div>
                        <strong>{message.authorDisplay}:&nbsp;&nbsp;</strong>
                        {message.message}
                      </div>
                      {errorMessage && isLastMessage ? (
                        <div className={messagingStyles.errorInTimestamp}>{errorMessage}</div>
                      ) : sendingMessage && isLastMessage ? (
                        <div className={messagingStyles.sendingTimestamp}>
                          <CircularProgress size={10} color="inherit" />
                          <span> sending...</span>
                        </div>
                      ) : (
                        <div
                          className={
                            message.isFromCurrentUser
                              ? messagingStyles.timeStampUser
                              : messagingStyles.timeStampSystem
                          }
                        >
                          {formattedTime} · {formattedDate}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {!conversationLoading && (
            <div className={messagingStyles.messageInputArea}>
              {isClosed && (
                <p style={{ marginBottom: 12, fontSize: 14, color: '#505050' }}>
                  This concern is closed. You can read the thread but cannot send new messages.
                </p>
              )}
              <div className={messagingStyles.messageInputContainer}>
                <textarea
                  className={messagingStyles.messageInput}
                  placeholder={isClosed ? 'Thread closed' : 'Type a message'}
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  disabled={conversationLoading || sendingMessage || isClosed}
                  rows={5}
                />
                <DefaultButton
                  className={messagingStyles.sendButton}
                  onClick={handleSendMessage}
                  disabled={
                    messageText.trim().length === 0 ||
                    conversationLoading ||
                    sendingMessage ||
                    isClosed
                  }
                >
                  <SendIcon className={messagingStyles.sendIcon} />
                </DefaultButton>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};
